import { DEFAULT_TIMEZONE, DEFAULT_TIMEZONE_ABBR } from './constants';
import { normalizeTimeZone } from './normalize';

// gloss
export const formatTimezoneAbbr = (timezone?: string | null, date: Date = new Date()): string => {
  if (!timezone) return DEFAULT_TIMEZONE_ABBR;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(date);
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    return tzPart?.value || timezone;
  } catch {
    return DEFAULT_TIMEZONE_ABBR;
  }
};

// gloss
export const getFriendlyTimeZoneName = (timezone: string | null | undefined): string => {
  if (!timezone) return '';
  try {
    const now = new Date();
    const parts = now.toLocaleDateString('en-US', { timeZoneName: 'long', timeZone: timezone });
    return parts.split(',').pop()?.trim() ?? timezone;
  } catch {
    return timezone;
  }
};

// gloss
export const convertToLocalTimeZone = (
  utcIsoDate: string,
  userTimeZone: string,
): { date: string; time: string; utcIsoDate: string; localIsoDate: string } => {
  const date = new Date(utcIsoDate);

  const localDate = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: userTimeZone,
  });

  const localTime = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: userTimeZone,
  });

  return { date: localDate, time: localTime, utcIsoDate, localIsoDate: date.toISOString() };
};

// gloss
export const formatDateTime = (date: string | Date | null | undefined, timezone?: string | null): string => {
  if (!date) return 'Not yet';
  try {
    const opts: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    };
    if (timezone) {
      opts.timeZone = normalizeTimeZone(timezone);
      opts.weekday = 'short';
    }
    return new Date(date).toLocaleString('en-US', opts);
  } catch {
    return new Date(date).toISOString();
  }
};

// gloss
export const formatDateTimeShort = (
  date: string | Date | null | undefined,
  timezone?: string | null,
): string | null => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  const tz = timezone ? normalizeTimeZone(timezone) : DEFAULT_TIMEZONE;
  const wallClock = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
  return `${wallClock} ${formatTimezoneAbbr(tz, d)}`;
};
